/**
 * 跨页签通信工具
 * 使用 BroadcastChannel API 实现同域名下不同页签之间的通信
 */

// 广播通道名称
const CHANNEL_NAME = 'tenant_sync_channel';

// 消息类型
export type CrossTabMessageType = 'TENANT_SWITCH' | 'TENANT_SWITCH_SUCCESS';

export interface CrossTabMessage {
  type: CrossTabMessageType;
  payload?: {
    tenantId?: string;
    tenantDomain?: string;
    timestamp?: number;
  };
}

class CrossTabCommunication {
  private channel: BroadcastChannel | null = null;

  private onMessageCallback: ((message: CrossTabMessage) => void) | null = null;

  /**
   * 初始化跨页签通信
   * @param callback 接收到消息时的回调函数
   */
  init(callback: (message: CrossTabMessage) => void): void {
    // 检查浏览器是否支持 BroadcastChannel
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('当前浏览器不支持 BroadcastChannel，跨页签同步功能不可用');
      return;
    }

    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.onMessageCallback = callback;

    this.channel.onmessage = (event: MessageEvent) => {
      const message = event.data as CrossTabMessage;
      console.log('收到跨页签消息:', message);
      if (this.onMessageCallback) {
        this.onMessageCallback(message);
      }
    };

    this.channel.onmessageerror = (event) => {
      console.error('跨页签消息接收错误:', event);
    };
  }

  /**
   * 发送消息到其他页签
   * @param message 消息内容
   */
  postMessage(message: CrossTabMessage): void {
    if (!this.channel) {
      console.warn('跨页签通信未初始化，无法发送消息');
      return;
    }

    const messageWithTimestamp = {
      ...message,
      payload: {
        ...message.payload,
        timestamp: Date.now(),
      },
    };

    console.log('发送跨页签消息:', messageWithTimestamp);
    this.channel.postMessage(messageWithTimestamp);
  }

  /**
   * 通知其他页签租户已切换
   * @param tenantId 新租户ID
   * @param tenantDomain 新租户对应的域名（可选）
   */
  notifyTenantSwitch(tenantId: string, tenantDomain?: string): void {
    this.postMessage({
      type: 'TENANT_SWITCH',
      payload: { tenantId, tenantDomain },
    });
  }

  /**
   * 销毁通信通道
   */
  destroy(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
      this.onMessageCallback = null;
    }
  }
}

// 导出单例
export const crossTabCommunication = new CrossTabCommunication();
